import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Filter, ArrowUpNarrowWide, ArrowDownWideNarrow, Eye } from "lucide-react";
import { listAllQuotes } from "../../reducers/quote.slice";
import { convertDateFormatInUtc, formatNumberToDollar, countActiveFiltersAndSort } from "../../utils/util";
import { AuthContext } from "../../context/authContext";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import PageHeader from "../../components/shared/PageHeader";
import StatusBadge from "../../components/shared/StatusBadge";
import StatCard from "../../components/shared/StatCard";
import SearchBar from "../../components/shared/SearchBar";
import RefreshButton from "../../components/shared/RefreshButton";
import DataTable from "../../components/shared/DataTable";
import useMediaQuery from "../../components/shared/useMediaQuery";

const STATUS_OPTIONS = [
  "QUOTE_PENDING",
  "QUOTE_APPROVED",
  "QUOTE_REJECTED",
  "QUOTE_ACCEPTED",
  "QUOTE_DENIED",
];

const QUOTE_STATUS_LABELS = {
  QUOTE_PENDING: "Pending",
  QUOTE_APPROVED: "Approved",
  QUOTE_ACCEPTED: "Accepted",
  QUOTE_REJECTED: "Rejected",
  QUOTE_DENIED: "Denied",
};

const SOON_THRESHOLD_HOURS = 24;
const ROWS_PER_PAGE_DEFAULT = 10;

const MemberQuotesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const [quotes, setQuotes] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const storedStatuses = sessionStorage.getItem("quotesMember_selectedStatuses");
    return storedStatuses ? JSON.parse(storedStatuses) : [];
  });
  const [sortDirection, setSortDirection] = useState(() => {
    return sessionStorage.getItem("quotesMember_sortDirection") || null;
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_DEFAULT);
  const [loading, setLoading] = useState(false);

  const handleViewQuote = (row) => {
    navigate("/dashboard/quotes/view-quotes", { state: { quoteId: row?.id } });
  };

  const getAllQuotes = () => {
    setLoading(true);
    dispatch(listAllQuotes()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setLoading(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setLoading(false);
      const formattedQuotes = res?.payload?.map((item) => {
        const createdDate = convertDateFormatInUtc(item?.created_at, "MMM D, YYYY");
        const quoteExpiryDate = item?.quote_expiry
          ? convertDateFormatInUtc(item.quote_expiry, "MMM D, YYYY") || "-"
          : "-";
        const quoteExpiryTime = item?.quote_expiry
          ? convertDateFormatInUtc(item.quote_expiry, "h:mm A")
          : "";

        const cashoutPrice = formatNumberToDollar(item?.cashout_price);
        const totalAmount = formatNumberToDollar(item?.total_amount);
        return {
          ...item,
          createdDate,
          cashoutPrice,
          quoteExpiryDate,
          quoteExpiryTime,
          totalAmount,
        };
      });
      setQuotes(formattedQuotes || []);
    });
  };

  useEffect(() => {
    getAllQuotes();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setSearchResults(quotes);
  }, [quotes]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!debouncedSearch || !debouncedSearch.trim()) {
        setSearchResults(quotes);
        return;
      }
      const query = debouncedSearch.toLowerCase();
      setSearchResults(
        quotes.filter((row) => {
          return ["upc", "item_name"].some((param) => {
            const value = row[param];
            return value && value.toString().toLowerCase().includes(query);
          });
        })
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [debouncedSearch, quotes]);

  useEffect(() => {
    let filtered = [...searchResults];

    if (selectedStatuses?.length > 0) {
      filtered = filtered.filter((item) => selectedStatuses.includes(item?.status));
    }

    if (sortDirection === "asc" || sortDirection === "desc") {
      filtered.sort((a, b) => {
        const valueA = parseFloat(a?.total_amount || 0);
        const valueB = parseFloat(b?.total_amount || 0);
        return sortDirection === "asc" ? valueA - valueB : valueB - valueA;
      });
    }

    setFilteredData(filtered);
    setCurrentPage(0);
  }, [searchResults, selectedStatuses, sortDirection]);

  useEffect(() => {
    sessionStorage.setItem("quotesMember_selectedStatuses", JSON.stringify(selectedStatuses));
  }, [selectedStatuses]);

  useEffect(() => {
    if (sortDirection) {
      sessionStorage.setItem("quotesMember_sortDirection", sortDirection);
    } else {
      sessionStorage.removeItem("quotesMember_sortDirection");
    }
  }, [sortDirection]);

  const handleStatusChange = (status, checked) => {
    setSelectedStatuses((prev) => {
      return checked ? [...prev, status] : prev.filter((s) => s !== status);
    });
  };

  const handleSortAscending = () => setSortDirection((prev) => prev === "asc" ? null : "asc");
  const handleSortDescending = () => setSortDirection((prev) => prev === "desc" ? null : "desc");

  const quoteStats = useMemo(() => {
    const now = Date.now();
    const active = quotes.filter((q) => q.status === "QUOTE_PENDING");
    const approved = quotes.filter((q) => q.status === "QUOTE_APPROVED");
    const quotedValue = active.reduce((sum, q) => sum + (parseFloat(q?.total_amount) || 0), 0);
    const expiringSoon = active.filter((q) => {
      if (!q?.quote_expiry) return false;
      const hoursLeft = (new Date(q.quote_expiry).getTime() - now) / 3600000;
      return hoursLeft > 0 && hoursLeft <= SOON_THRESHOLD_HOURS;
    }).length;

    return {
      activeCount: active.length,
      expiringSoon,
      quotedValue,
      approvedCount: approved.length,
    };
  }, [quotes]);

  const columns = [
    {
      key: "item_name",
      label: "Item Name",
      maxWidth: 150,
      render: (row) => (
        <a
          href={row?.item_link || "#"}
          target="_blank"
          rel="noopener noreferrer"
          title={row?.item_name}
          style={{
            color: "#4fb8f0",
            textDecoration: "underline",
            cursor: "pointer",
            fontSize: 12.5,
          }}
          onClick={(e) => {
            if (!row?.item_link) {
              showToast("Item Link not available");
              e.preventDefault();
              return;
            }
          }}
        >
          {row?.item_name}
        </a>
      ),
    },
    { key: "createdDate", label: "Order Date", maxWidth: 90 },
    {
      key: "quoteExpiryDate",
      label: "Quote Expiry Date",
      render: (row) => (
        <div style={{ lineHeight: 1.4 }}>
          <div>{row.quoteExpiryDate}</div>
          {row.quoteExpiryTime && (
            <div style={{ fontSize: 11, color: "#c3c8cc" }}>{row.quoteExpiryTime}</div>
          )}
        </div>
      ),
    },
    { key: "upc", label: "UPC", maxWidth: 100 },
    { key: "quantity", label: "Quantity" },
    { key: "cashoutPrice", label: "Cash Out Price" },
    { key: "totalAmount", label: "Total Amount" },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge status={row.status} label={QUOTE_STATUS_LABELS[row.status]} />,
    },
    {
      key: "action",
      label: "Action",
      align: "center",
      render: (row) => (
        <button
          type="button"
          aria-label="view quote details"
          onClick={(e) => {
            e.stopPropagation();
            handleViewQuote(row);
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 30,
            height: 30,
            borderRadius: 8,
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "#c3c8cc",
            cursor: "pointer",
            transition: "background 0.15s, color 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255,255,255,0.06)";
            e.currentTarget.style.color = "#fff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "#c3c8cc";
          }}
        >
          <Eye size={14} />
        </button>
      ),
    },
  ];

  const activeFilterCount = countActiveFiltersAndSort(sortDirection, selectedStatuses);
  const pagedRows = filteredData.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ marginBottom: 20 }}>
        <PageHeader title="Quotes" subtitle="Review and manage your active quotes." />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? "145px" : "180px"}, 1fr))`,
          gap: isMobile ? 12 : 16,
          marginBottom: 20,
        }}
      >
        <StatCard
          label="ACTIVE QUOTES"
          value={quoteStats.activeCount}
          accent="#00ADB5"
          detail={quoteStats.expiringSoon > 0 ? `${quoteStats.expiringSoon} expiring soon` : "none expiring soon"}
        />
        <StatCard
          label="QUOTED VALUE"
          value={formatNumberToDollar(quoteStats.quotedValue)}
          accent="#e8b84b"
          detail="if all accepted"
        />
        <StatCard
          label="APPROVED"
          value={quoteStats.approvedCount}
          accent="#4caf50"
          detail="ready to accept"
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: isMobile ? "stretch" : "center",
          flexWrap: "wrap",
          marginBottom: 20,
        }}
      >
        <div style={{ display: "flex", alignItems: isMobile ? "stretch" : "center", flexWrap: "wrap", width: isMobile ? "100%" : "auto", gap: isMobile ? 10 : 0 }}>
          <div style={{ display: "flex", alignItems: "center", flex: isMobile ? "1 1 0" : "0 0 auto", minWidth: 0, gap: isMobile ? 8 : 12 }}>
            {!isMobile && <RefreshButton onRefresh={getAllQuotes} />}
            <div style={{ flex: isMobile ? 1 : "none", minWidth: 0 }}>
              <SearchBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                setDebouncedTerm={setDebouncedSearch}
              />
            </div>
          </div>

          <div style={{ marginLeft: isMobile ? 0 : 20, flexShrink: 0 }}>
            <DropdownMenu open={filterOpen} onOpenChange={setFilterOpen}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter size={13} style={{ marginRight: 6 }} />
                  Filter & Sort
                  {activeFilterCount > 0 && (
                    <span
                      style={{
                        marginLeft: 6,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 16,
                        height: 16,
                        borderRadius: 8,
                        background: "#00ADB5",
                        color: "#000",
                        fontSize: 10,
                        fontWeight: 800,
                        padding: "0 4px",
                      }}
                    >
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="tw-w-64">
                <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                {STATUS_OPTIONS.map((status) => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={selectedStatuses.includes(status)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(checked) => handleStatusChange(status, checked)}
                  >
                    {QUOTE_STATUS_LABELS[status]}
                  </DropdownMenuCheckboxItem>
                ))}

                <DropdownMenuSeparator />

                <DropdownMenuItem active={sortDirection === "asc"} onClick={handleSortAscending}>
                  <ArrowUpNarrowWide size={14} style={{ marginRight: 8 }} />
                  Sort by Amount (Low to High)
                </DropdownMenuItem>
                <DropdownMenuItem active={sortDirection === "desc"} onClick={handleSortDescending}>
                  <ArrowDownWideNarrow size={14} style={{ marginRight: 8 }} />
                  Sort by Amount (High to Low)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div style={{ marginLeft: isMobile ? 0 : 20, width: isMobile ? "100%" : "auto" }}>
            <Button onClick={() => navigate("/dashboard/quotes/quotes-form")} style={{ width: isMobile ? "100%" : "auto" }}>
              Request Quotes
            </Button>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={pagedRows}
        totalRows={filteredData.length}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={(newPage) => setCurrentPage(newPage)}
        onRowsPerPageChange={(newValue) => {
          setRowsPerPage(newValue);
          setCurrentPage(0);
        }}
        loading={loading}
      />
    </div>
  );
};

export default MemberQuotesPage;
